import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  useMarkAsSubmittedCIS300,
  useSubmitCIS300,
} from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetDashboardPaymentListParams } from '@nxsys/store/slices/dashboard/types';
import {
  MarkAsSubmittedCIS300Params,
  SubmitCIS300Params,
} from '@nxsys/types/api/dashboard';
import { ICompany } from '@nxsys/types/models/company';
import { IPayroll } from '@nxsys/types/models/payroll';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, toNumber } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const CIS300Modal: React.FC<Props> = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { paymentList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = paymentList;

  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(currentTaxYear);
  const [selectedTaxMonth, setSelectedTaxMonth] = useState<any>(null);

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
        companyPaymentId: selectedCompany?.id,
        taxYear: selectedTaxYear || undefined,
        taxMonth: selectedTaxMonth?.value || undefined,
        startTaxMonth: selectedTaxMonth?.startTaxMonth || undefined,
        endTaxMonth: selectedTaxMonth?.endTaxMonth || undefined,
      });
  }, [isOpen, selectedCompany, selectedTaxYear, selectedTaxMonth]);

  const handleFetchData = (params: GetDashboardPaymentListParams) => {
    const newParams = {
      ...params,
      isCis300: true,
    };

    dispatch(dashboardActions.getDashboardPaymentList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const columns: ColumnsType<IPayroll> = [
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id || '-',
    },
    {
      key: 'subcontractorRef',
      title: 'Employee Name',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      dataIndex: 'subcontractorRef',
      render: (value) =>
        value?.fullName || `${value?.surname || '-'} ${value?.forename || ''}`,
    },
    {
      title: 'UTR No',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.setting?.UTRNo || '-',
    },
    {
      title: 'Personal Verification No',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.setting?.personalVerificationNumber || '-',
    },
    {
      title: 'Payment Company ID',
      dataIndex: 'companyRefId',
      render: (value) => value || '-',
    },

    {
      key: 'gross',
      title: 'Gross',
      dataIndex: 'gross',
      align: 'right' as const,
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)) || '-',
    },
    {
      key: 'materials',
      title: 'Materials',
      dataIndex: 'materials',
      align: 'right' as const,
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
    {
      key: 'cisDeduction',
      title: 'CIS Deduction',
      dataIndex: 'cisDeduction',
      align: 'right' as const,
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
  ];

  const taxMonthOptions = [
    { label: 'Tax Month 1', value: 1, startTaxMonth: `06/04/${currentTaxYear || ''}`, endTaxMonth: `05/05/${currentTaxYear || ''}` },
    { label: 'Tax Month 2', value: 2, startTaxMonth: `06/05/${currentTaxYear || ''}`, endTaxMonth: `05/06/${currentTaxYear || ''}` },
    { label: 'Tax Month 3', value: 3, startTaxMonth: `06/06/${currentTaxYear || ''}`, endTaxMonth: `05/07/${currentTaxYear || ''}` },
    { label: 'Tax Month 4', value: 4, startTaxMonth: `06/07/${currentTaxYear || ''}`, endTaxMonth: `05/08/${currentTaxYear || ''}` },
    { label: 'Tax Month 5', value: 5, startTaxMonth: `06/08/${currentTaxYear || ''}`, endTaxMonth: `05/09/${currentTaxYear || ''}` },
    { label: 'Tax Month 6', value: 6, startTaxMonth: `06/09/${currentTaxYear || ''}`, endTaxMonth: `05/10/${currentTaxYear || ''}` },
    { label: 'Tax Month 7', value: 7, startTaxMonth: `06/10/${currentTaxYear || ''}`, endTaxMonth: `05/11/${currentTaxYear || ''}` },
    { label: 'Tax Month 8', value: 8, startTaxMonth: `06/11/${currentTaxYear || ''}`, endTaxMonth: `05/12/${currentTaxYear || ''}` },
    { label: 'Tax Month 9', value: 9, startTaxMonth: `06/12/${currentTaxYear || ''}`, endTaxMonth: `05/01/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 10', value: 10, startTaxMonth: `06/01/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/02/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 11', value: 11, startTaxMonth: `06/02/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/03/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 12', value: 12, startTaxMonth: `06/03/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/04/${currentTaxYear + 1 || ''}` },
  ];

  const handleExport = () => {
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }
    const dataExport = list.map((item) => ({
      subcontractorId: item?.subcontractorRef?.id || '',
      subcontractorName:
        item?.subcontractorRef?.fullName ||
        `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname
        }`,
      UTRNo: item?.subcontractorRef?.setting?.UTRNo || '',
      personalVerificationNo:
        item?.subcontractorRef?.setting?.personalVerificationNumber || '',
      companyName: item?.subcontractorRef?.setting?.companyPayment,
      gross: item?.gross
        ? formatMoney(
            toNumber(item?.gross),
            showCurrencyByCountry(payrollCountry),
          )
        : formatMoney(0, showCurrencyByCountry(payrollCountry)),
      material: item?.materials
        ? formatMoney(
            toNumber(item?.materials),
            showCurrencyByCountry(payrollCountry),
          )
        : formatMoney(0, showCurrencyByCountry(payrollCountry)),
      cisDeduction: item?.cisDeduction
        ? formatMoney(
            toNumber(item?.cisDeduction),
            showCurrencyByCountry(payrollCountry),
          )
        : formatMoney(0, showCurrencyByCountry(payrollCountry)),
    }));
    const headers = [
      'Employee ID',
      'Employee Name',
      'UTR No',
      'Personal Verification No',
      'Payment Company ID',
      'Gross',
      'Materials',
      'CIS Deduction',
    ];
    const csvExporter = new ExportToCsv({
      filename: `Payroll-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const { run: submitCIS300, loading: submitCIS300Loading } = useSubmitCIS300();
  const handleSubmitCIS300 = async () => {
    if (!selectedCompany || !selectedTaxYear || !selectedTaxMonth) {
      showMessage({
        type: 'warning',
        content: 'Please select company, tax year and tax month',
      });
      return;
    }
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }

    const params: SubmitCIS300Params = {
      companyId: selectedCompany?.id || '',
      taxYear: selectedTaxYear || 0,
      taxMonth: selectedTaxMonth?.value,
      startTaxMonth: selectedTaxMonth?.startTaxMonth,
      endTaxMonth: selectedTaxMonth?.endTaxMonth,
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    };
    const res = await submitCIS300(params);
    if (res?.success) {
      showMessage({
        type: 'success',
        content: 'Submit CIS 300 successfully',
      });
    } else {
      showMessage({
        type: 'error',
        content: res?.data?.message,
      });
    }
    refetchData();
  };

  const { run: markAsSubmittedCIS300, loading: markAsSubmittedCIS300Loading } =
    useMarkAsSubmittedCIS300();
  const handleMarkAsSubmittedCIS300 = async () => {
    if (!selectedCompany || !selectedTaxYear || !selectedTaxMonth) {
      showMessage({
        type: 'warning',
        content: 'Please select company, tax year and tax month',
      });
      return;
    }
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }

    const params: MarkAsSubmittedCIS300Params = {
      companyId: selectedCompany?.id || '',
      taxYear: selectedTaxYear || 0,
      taxMonth: selectedTaxMonth?.value,
      startTaxMonth: selectedTaxMonth?.startTaxMonth,
      endTaxMonth: selectedTaxMonth?.endTaxMonth,
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    };
    const res = await markAsSubmittedCIS300(params);
    if (res?.success) {
      showMessage({
        type: 'success',
        content: 'Mark as Submitted successfully',
      });
      refetchData();
    }
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">CIS 300</div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'submit',
                  label: 'Submit',
                  onClick: handleSubmitCIS300,
                },
                {
                  key: 'markAsSubmitted',
                  label: 'Mark as Submitted',
                  onClick: handleMarkAsSubmittedCIS300,
                },
                {
                  key: 'export',
                  label: 'Export',
                  onClick: handleExport,
                },
              ],
            }}
          >
            <AppButton name="Actions" rightIcon={<DownOutlined />} />
          </Dropdown>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      {(submitCIS300Loading || markAsSubmittedCIS300Loading) && (
        <AppLoader />
      )}
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full flex-wrap my-[20px] px-[10px]">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder="Select Payment Company"
              onChangeValue={(value: any) => {
                setSelectedCompany(value?.fullData);
              }}
              paramFilter={{ enableRTI: true }}
            />
            <TaxYearSelect
              placeholder="Select Tax Year"
              onFilter={(e) => setSelectedTaxYear(e?.taxYear || null)}
              allowClear
            />
            <AppSelect
              options={taxMonthOptions}
              placeholder="Select Tax Month"
              className="!min-w-0 !w-fit"
              onChange={(value) => setSelectedTaxMonth(value)}
            />
          </div>
          <AppInputSearch onChange={handleSearch} />
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1800 }}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default CIS300Modal;
